import { useContentfulContentTypeEntries, useContentfulContentTypeEntry } from '@order/Contentful';

import {
  validAccountMenuData as accountMenuDataFallback,
  validContentfulData as orgLinksDataFallback,
} from './fallback';

export function useOrganizationLinksFooterContentfulData() {
  const { data: possibleContentfulData } =
    useContentfulContentTypeEntry<ContentfulOrganizationLinksFooter>({
      contentType: ORGANIZATION_LINKS_FOOTER_CONTENT_ID,
      include: 2,
    });

  // ─── Live Contentful Footer Data ─────────────────────────────────────

  const liveFooter = convertFooter(possibleContentfulData?.fields);

  if (liveFooter.sections.length > 0) {
    return liveFooter;
  }

  // ─── Fallback Contentful Footer Data ─────────────────────────────────

  return convertFooter(orgLinksDataFallback.fields);
}

export function useOrganizationLinksAccountMenuContentfulData() {
  const { data: possibleContentfulData } =
    useContentfulContentTypeEntries<ContentfulOrganizationLinksFooterSectionData>({
      contentType: ORGANIZATION_LINKS_FOOTER_SECTION_CONTENT_ID,
      include: 2,
    });

  const accountMenuData = possibleContentfulData?.find(
    (data) => data?.fields?.name === ORGANIZATION_LINKS_ACCOUNT_MENU,
  );

  if (!accountMenuData?.fields) {
    return convertSection(accountMenuDataFallback);
  }

  return convertSection(
    accountMenuData as { fields: ContentfulOrganizationLinksFooterSectionData },
  );
}

// ─── Converters ────────────────────────────────────────────────────────

function convertFooter(
  footer?: ContentfulOrganizationLinksFooter,
): ContentfulOrganizationLinksFooterData {
  const sections = footer?.sections ?? [];

  return { sections: sections.filter(hasSectionFields).map(convertSection) };
}

function convertSection(section: ContentfulOrganizationLinksFooterSection) {
  return {
    name: section.fields.name,
    links: section.fields.links.map(convertLink),
    conditionalLinks: section.fields.conditionalLinks?.map(convertLink),
  };
}

function convertLink(link: ContentfulOrganizationLinksFooterLink) {
  return {
    id: link.fields.id,
    label: link.fields.label,
    url: link.fields.url,
    icon: link.fields.icon?.fields?.file?.url,
  };
}

function hasSectionFields(section: ContentfulOrganizationLinksFooterSection) {
  return Boolean(section.fields);
}

// ─── Types ─────────────────────────────────────────────────────────────

type ContentfulOrganizationLinksFooter = Readonly<{
  name?: string;
  sections: readonly ContentfulOrganizationLinksFooterSection[];
}>;

type ContentfulOrganizationLinksFooterSectionData = Readonly<{
  name: string;
  links: readonly ContentfulOrganizationLinksFooterLink[];
  conditionalLinks?: readonly ContentfulOrganizationLinksFooterLink[];
}>;

type ContentfulOrganizationLinksFooterSection = Readonly<{
  name?: string;
  fields: ContentfulOrganizationLinksFooterSectionData;
}>;

type ContentfulOrganizationLinksFooterLink = Readonly<{
  name?: string;
  fields: {
    id?: string;
    label: string;
    url: string;
    icon?: { fields?: { file?: { url?: string } } };
  };
}>;

type ContentfulOrganizationLinksFooterData = Readonly<{
  sections: ReadonlyArray<
    Readonly<{
      name: string;
      links: ReadonlyArray<
        Readonly<{
          id?: string;
          label: string;
          url: string;
          icon?: string;
        }>
      >;
      conditionalLinks?: ReadonlyArray<
        Readonly<{
          id?: string;
          label: string;
          url: string;
          icon?: string;
        }>
      >;
    }>
  >;
}>;

// ─── Constants ─────────────────────────────────────────────────────────

const ORGANIZATION_LINKS_FOOTER_CONTENT_ID = 'organizationLinksFooter';
const ORGANIZATION_LINKS_FOOTER_SECTION_CONTENT_ID = 'organizationLinksFooterSection'; // prettier-ignore
const ORGANIZATION_LINKS_ACCOUNT_MENU = 'Account Menu';
